import React from 'react';
import { Rocket, Globe, Users, Zap, TrendingUp, Shield } from 'lucide-react';
import { Card } from './ui/card';

const FutureVision = () => {
  const visionPoints = [
    {
      icon: Globe,
      title: "Cross-Platform Integration",
      description: "Seamless integration with YouTube, Twitch, TikTok, and all major platforms"
    },
    {
      icon: Users,
      title: "Creator Collaboration Hub",
      description: "Manage collaborations, rights, and revenue sharing in one place"
    },
    {
      icon: Shield,
      title: "Smart Rights Management",
      description: "Automated rights tracking and royalty distribution for all appearances"
    },
    {
      icon: TrendingUp,
      title: "Fair Compensation Algorithm",
      description: "AI-powered suggestions for fair compensation based on reach and engagement"
    },
    {
      icon: Zap,
      title: "Instant Verification",
      description: "Real-time appearance detection using advanced AI and face recognition"
    },
    {
      icon: Rocket,
      title: "Industry Standard",
      description: "Becoming the trusted infrastructure for the entire creator economy"
    }
  ];

  return (
    <section className="relative py-24 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a2e] via-[#1a0a4e] to-[#0a0a2e]"></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] bg-gradient-to-r from-cyan-500/10 via-purple-500/10 to-pink-500/10 rounded-full blur-3xl"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16 space-y-6">
          <span className="px-4 py-2 rounded-full bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 text-purple-400 text-sm font-medium inline-block backdrop-blur-sm">
            The Future
          </span>
          <h2 className="text-4xl lg:text-6xl font-bold text-white">
            Building the Infrastructure for the
            <br />
            <span className="bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-500 bg-clip-text text-transparent">
              Creator Economy
            </span>
          </h2>
          <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            Stream Shares will evolve into the comprehensive platform that manages creator collaborations, 
            appearances, rights, and fair compensation across all social media and streaming platforms.
          </p>
        </div>

        {/* Vision Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {visionPoints.map((point, index) => {
            const Icon = point.icon;
            const colors = [
              { from: "from-cyan-500/20", to: "to-blue-500/20", border: "border-cyan-500/30", text: "text-cyan-400", glow: "bg-cyan-500/20" },
              { from: "from-blue-500/20", to: "to-purple-500/20", border: "border-blue-500/30", text: "text-blue-400", glow: "bg-blue-500/20" },
              { from: "from-purple-500/20", to: "to-pink-500/20", border: "border-purple-500/30", text: "text-purple-400", glow: "bg-purple-500/20" },
              { from: "from-pink-500/20", to: "to-red-500/20", border: "border-pink-500/30", text: "text-pink-400", glow: "bg-pink-500/20" },
              { from: "from-indigo-500/20", to: "to-cyan-500/20", border: "border-indigo-500/30", text: "text-indigo-400", glow: "bg-indigo-500/20" },
              { from: "from-fuchsia-500/20", to: "to-pink-500/20", border: "border-fuchsia-500/30", text: "text-fuchsia-400", glow: "bg-fuchsia-500/20" }
            ];
            const color = colors[index % colors.length];

            return (
              <Card 
                key={index}
                className={`group relative bg-gradient-to-br ${color.from} ${color.to} backdrop-blur-sm border ${color.border} p-6 hover:scale-105 transition-all duration-300 hover:shadow-2xl overflow-hidden`}
              >
                {/* Icon */}
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${color.from} ${color.to} border ${color.border} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <Icon className={`w-7 h-7 ${color.text}`} />
                </div>

                {/* Content */}
                <h3 className={`text-xl font-bold ${color.text} mb-3`}>
                  {point.title}
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  {point.description}
                </p>

                {/* Decorative Glow */}
                <div className={`absolute -bottom-4 -right-4 w-32 h-32 ${color.glow} rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity`}></div>
              </Card>
            );
          })}
        </div>

        {/* Roadmap Timeline */}
        <div className="relative mt-20 p-12 rounded-3xl bg-gradient-to-br from-gray-900/80 to-gray-800/80 border border-gray-700/50 backdrop-blur-sm">
          <h3 className="text-3xl font-bold text-white mb-12 text-center">
            Development Roadmap
          </h3>

          <div className="space-y-8">
            {[
              { phase: "Phase 1", title: "Platform Launch", status: "Current", color: "cyan", items: ["Core platform features", "Creator & viewer registration", "Basic appearance logging"] },
              { phase: "Phase 2", title: "AI Integration", status: "Q2 2025", color: "purple", items: ["Automated appearance detection", "Smart verification system", "Cross-platform sync"] },
              { phase: "Phase 3", title: "Full Ecosystem", status: "Q4 2025", color: "pink", items: ["Rights management", "Revenue sharing tools", "Industry partnerships"] }
            ].map((roadmap, index) => {
              const colorClasses = {
                cyan: "from-cyan-500 to-blue-500",
                purple: "from-purple-500 to-pink-500",
                pink: "from-pink-500 to-red-500"
              };

              return (
                <div key={index} className="flex items-start gap-6 group">
                  <div className={`flex-shrink-0 w-20 h-20 rounded-2xl bg-gradient-to-br ${colorClasses[roadmap.color]} flex items-center justify-center text-white font-bold text-lg group-hover:scale-110 transition-transform shadow-lg`}>
                    {index + 1}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="text-2xl font-bold text-white">{roadmap.phase}: {roadmap.title}</h4>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        roadmap.status === 'Current' 
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                          : 'bg-gray-700/50 text-gray-400 border border-gray-600/30'
                      }`}>
                        {roadmap.status}
                      </span>
                    </div>
                    <ul className="space-y-2">
                      {roadmap.items.map((item, i) => (
                        <li key={i} className="text-gray-300 flex items-center gap-2">
                          <div className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${colorClasses[roadmap.color]}`}></div>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Vision Statement */}
        <div className="mt-16 text-center">
          <div className="inline-block p-12 rounded-3xl bg-gradient-to-r from-cyan-500/10 via-purple-500/10 to-pink-500/10 border border-cyan-500/20 backdrop-blur-sm">
            <Rocket className="w-16 h-16 text-purple-400 mx-auto mb-6" />
            <p className="text-2xl text-white font-medium max-w-3xl">
              "Our mission is to create the standard infrastructure that makes the creator economy 
              <span className="bg-gradient-to-r from-cyan-400 to-pink-500 bg-clip-text text-transparent font-bold"> fair, transparent, and dispute-free </span>
              for everyone involved."
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FutureVision;
